import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import clsx from 'clsx';
import i18n from 'i18n-react';
import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
  AccordionItemState,
} from 'react-accessible-accordion';
import { Spring } from 'react-spring';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// import BadgeDisplay from 'components/BadgeDisplay';
import Progress from 'components/Progress';
import ItineraryModuleCard from 'components/TrainingGrid/ItineraryModuleCard';
import TrainingCard from 'components/TrainingGrid/TrainingCard';
import TrainingCategoryCard from 'components/TrainingGrid/TrainingCategoryCard';
import TrainingExtraCard from 'components/TrainingGrid/TrainingExtraCard';

import APIHandler from 'config/APIHandler';
import DataHandler from 'config/DataHandler';
import StorageHandler from 'config/StorageHandler';

import { getExternalAppChapterUrl } from 'helpers/externalAppUrl';
import { useChapters } from 'hooks/useChapters';
import { useSettings } from 'hooks/useSettings';
import { useUserInfo } from 'hooks/useUserInfo';

import { storageKeys } from 'settings';

const trackShare = (chapterNumber, name) => {
  const value = `chapter-${chapterNumber}`;
  DataHandler.matchAppId().then((appId) => {
    StorageHandler.load(storageKeys.user).then((userObj) => {
      let email = 'Guest';
      ({ email } = userObj);
      APIHandler.trackEvent(appId, email, 'retention', 'chapter', `${window.location.href}`, name).then(() => {});
    });
  });
};

const SubProgressBar = ({ type = null, progress }) => (
  <div className={`subprogress ${type}`}>
    <Progress id="subprogress" value={progress} rounded />
    <p>
      {progress} {i18n.translate('trainingScreens.percentageComplete')}
    </p>
  </div>
);

SubProgressBar.propTypes = {
  progress: PropTypes.number.isRequired,
  type: PropTypes.oneOf([null, 'bronze', 'silver', 'gold']),
};

const RetainGrid = () => {
  const { appId, lang } = useSettings();
  const { user: { email } } = useUserInfo();
  const { langFile } = lang || {};
  const {
    chapters,
    categories,
    subapps,
    itineraries = [],
    groups: { main, sub, extra },
  } = useChapters();

  // takes a list of chapters and returns the total number of chapters and the number of completed chapters.
  const getItemsFromChapters = (chapterList) =>
    chapterList.reduce(
      (acc, chapter) => {
        const { percentage } = chapter;
        acc.totalItemCount += 1;
        if (percentage === 100) acc.totalItemCountCompleted += 1;
        return acc;
      },
      { totalItemCountCompleted: 0, totalItemCount: 0 },
    );

  // calculates the completion rate of a category based on the chapters in the category
  const calculateGroupCompletionRate = (group) => {
    const { chapterIDs = [] } = group || {};
    const groupChapters = chapters.filter(({ id }) => chapterIDs.includes(id));

    const { totalItemCountCompleted, totalItemCount } = getItemsFromChapters(groupChapters);

    if (totalItemCount === 0) return 0;

    return (totalItemCountCompleted / totalItemCount) * 100;
  };

  const foundationalCompletionRate = calculateGroupCompletionRate(main);
  const specialistCompletionRate = calculateGroupCompletionRate(sub);
  const extraCompletionRate = calculateGroupCompletionRate(extra);

  const renderChapterLink = (chapter, className) => {
    const classes = clsx('trainingCard__Animated', className);
    const { id, percentage, comingSoon, video, title } = chapter;

    const lockedSubtitle = comingSoon
      ? i18n.translate('trainingScreens.comingSoon')
      : i18n.translate('trainingScreens.unlock');

    const locked = comingSoon || percentage < 100;

    return (
      <div
        className={classes}
        id={`ch${id}`}
        key={`ch${id}`}
        data-fancybox={video}
        data-src={video}
        onClick={() => trackShare(id, title)}
      >
        <TrainingCard
          appId={appId}
          chapter={chapter}
          redirPath="/main/retain/chapter/"
          lockedSubtitle
          lockedSubtitleOverride={lockedSubtitle}
          locked={locked}
        />
      </div>
    );
  };

  // render function for an entire category. (to subpage)
  const renderCategoryLink = (category, className) => {
    const classes = clsx('trainingCard__Animated', className);

    // eslint-disable-next-line no-unused-vars
    const { id, chapterIDs, title } = category;
    const categoryChapters = chapters.filter(({ id: chapterID }) => chapterIDs.includes(chapterID));

    // Unlock the category for retain if at least 1 chapter has been completed
    const hasCompletedChapter = categoryChapters.some(({ percentage: chapterPercentage }) => chapterPercentage === 100);
    const locked = !hasCompletedChapter;

    return (
      <div className={classes} key={`ca${id}`} onClick={() => trackShare(`experience-${id}`, title)}>
        <TrainingCategoryCard
          appId={appId}
          category={category}
          chapters={categoryChapters}
          redirPath="/main/training/extra"
          lockedSubtitle
          lockedSubtitleOverride={i18n.translate('trainingScreens.categoryLockMinimum', { number: 1 })}
          locked={locked}
          foundationLocked={foundationalCompletionRate < 100}
        />
      </div>
    );
  };

  const renderSubappsCategoryLink = (subapp, className, locked = false) => {
    const classes = clsx('trainingCard__Animated', className);
    const { id, title } = subapp;
    const lockedSubtitleText = i18n.translate('trainingScreens.categoryLockMinimum', { number: 1 });
    return (
      <div className={classes} key={`ex${id}`} onClick={() => trackShare(`partner-${id}`, title)}>
        <TrainingExtraCard
          appId={appId}
          module={subapp}
          redirPath="/main/retain/subapps/"
          locked={locked}
          lockedSubtitle
          lockedSubtitleOverride={lockedSubtitleText}
          foundationLocked={foundationalCompletionRate < 100}
        />
      </div>
    );
  };

  const renderSubapps = (style) => {
    const links = subapps.map((subapp) => {
      const { modules } = subapp || {};

      // Unlock the category for retain if at least 1 chapter has been completed
      const hasCompletedModule = modules.some(({ percentage }) => percentage === 100);
      const locked = !hasCompletedModule;
      return renderSubappsCategoryLink(subapp, `trainingGrid__training-card--${style}`, locked);
    });
    return links;
  };

  const renderItineraryCategoryLink = (category, className, locked) => {
    const classes = clsx('trainingCard__Animated', className);
    const { id, title } = category;
    const lockedSubtitleText = i18n.translate('trainingScreens.categoryLockMinimum', { number: 1 });
    return (
      <div className={classes} key={`itinerary-${id}`} onClick={() => trackShare(`itinerary-${id}`, title)}>
        <TrainingExtraCard
          appId={appId}
          module={category}
          redirPath="/main/retain/itineraries/"
          linkPathSegment="itineraries"
          locked={locked}
          lockedSubtitle
          lockedSubtitleOverride={lockedSubtitleText}
          foundationLocked={foundationalCompletionRate < 100}
        />
      </div>
    );
  };

  const renderItineraryLinks = (style) => {
    const styleClass = `trainingGrid__training-card--${style}`;
    return itineraries.flatMap((category) => {
      const openableSection = category.openableSection !== false;
      if (openableSection) {
        const { modules = [] } = category || {};
        const locked = foundationalCompletionRate < 100 || modules.length === 0;
        return [renderItineraryCategoryLink(category, styleClass, locked)];
      }
      const { modules = [] } = category;
      return modules.map((module) => (
        <ItineraryModuleCard
          key={module.id}
          appId={appId}
          module={module}
          redirPath={getExternalAppChapterUrl({
            subAppId: (module.sub_app && module.sub_app.id) || module.sessionAppId || '',
            chapterId: module.session_chapter_id != null ? module.session_chapter_id : 0,
            totalCount: (module.count && module.count.total_count) != null ? module.count.total_count : 0,
            highestPage: (module.progress && module.progress.highest_page) != null ? module.progress.highest_page : 0,
            email,
            langFile,
          })}
          linkPathSegment="itineraries"
          className={styleClass}
          foundationLocked={foundationalCompletionRate < 100}
          locked={foundationalCompletionRate < 100}
        />
      ));
    });
  };

  // render function for a group. Checks if the group should be rendered as a whole category, or if it should render the chapters individually. style is added to allow for styling.
  const renderGroup = (group, style) => {
    const { categoryIDs = [] } = group || {};
    const groupCategories = categories.filter(({ id }) => categoryIDs.includes(id));

    const links = groupCategories.map((category) => {
      const { openableSection, chapterIDs } = category;

      // openableSection is a boolean that determines if the category is 'openable', meaning it should be render the category as a whole. Else, it should render the chapters directly.
      if (openableSection) {
        return renderCategoryLink(category, clsx('specialization', `trainingGrid__training-card--${style}`));
      }

      return chapters
        .filter((chapter) => {
          const { id } = chapter;
          return chapterIDs.includes(id);
        })
        .map((chapter) => renderChapterLink(chapter, `trainingGrid__training-card--${style}`));
    });
    return links;
  };

  const foundationalLinks = renderGroup(main, 'main');
  const specialistLinks = renderGroup(sub, 'sub');
  const extraLearningLinks = renderSubapps('extra');
  const itineraryLinks = renderItineraryLinks('extra');

  // opens the accordion panels based on the completion rate of the chapters
  const opened = useMemo(() => {
    const requiredProgress = 95;
    const base = [];

    if (
      foundationalCompletionRate >= requiredProgress &&
      specialistCompletionRate >= requiredProgress &&
      extraCompletionRate >= requiredProgress
    ) {
      base.push('foundational', 'specialized', 'extra');
    } else if (foundationalCompletionRate < requiredProgress) {
      base.push('foundational');
    } else if (specialistCompletionRate < requiredProgress && extraCompletionRate < requiredProgress) {
      base.push('specialized', 'extra');
    } else if (specialistCompletionRate < requiredProgress) {
      base.push('specialized');
    } else if (extraCompletionRate < requiredProgress) {
      base.push('extra');
    } else {
      base.push('extra');
    }
    if (itineraryLinks.length > 0 && !base.includes('itineraries')) {
      base.push('itineraries');
    }
    return base;
  }, [extraCompletionRate, foundationalCompletionRate, specialistCompletionRate, itineraryLinks.length]);

  // On open page track event
  React.useEffect(() => {
    trackShare('page', 'Retain');
  }, []);

  return (
    <Spring from={{ transform: 'translateY(30%)', opacity: 0 }} to={{ transform: 'translateY(0%)', opacity: 1 }}>
      {() => (
        <React.Fragment>
          {/* (temporarily?) removed the badge display */}
          {/* {initialized && tiers && (
            <BadgeDisplay
              tierBadges={tierBadges}
              tierIndex={tierIndex}
              tiers={tiers}
              totalPoints={totalPoints}
              pointsBarSubtitle={i18n.translate('points.completed', {
                completed: totalItemCountCompleted,
                total: totalItemCount,
              })}
              layout="pillar"
              position="right"
            />
          )} */}
          {/* allowMultipleExpanded allowZeroExpanded : wWill allow multiple expanded tabs and/or zero expanded tabs. Insert in accordion as desired */}
          <Accordion preExpanded={opened} key={opened}>
            <div>
              <div className="trainingGrid">
                <AccordionItem uuid="foundational">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      <div className="subheader bronzeHeader">
                        <h1>{i18n.translate('trainingScreens.essential')}</h1>
                        {/* <h2>{i18n.translate('trainingScreens.essentialSub')}</h2>
                        <SubProgressBar type="bronze" progress={foundationalCompletionRate} /> */}
                        <AccordionItemState>
                          {({ expanded }) =>
                            !expanded ? (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                              </div>
                            ) : (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-up']} />
                              </div>
                            )
                          }
                        </AccordionItemState>
                      </div>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="flexer">
                      <div className="trainingGrid trainingGrid--main trainingGrid--8-col">{foundationalLinks} </div>
                    </div>
                  </AccordionItemPanel>
                </AccordionItem>

                <AccordionItem uuid="specialized">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      <div className="subheader sub silverHeader">
                        <h1>{i18n.translate('contentHeaders.subtrainings')}</h1>
                        {/* <h2>{i18n.translate('pageDescriptions.subtrainings')}</h2>
                        <SubProgressBar type="silver" progress={specialistCompletionRate} /> */}

                        <AccordionItemState>
                          {({ expanded }) =>
                            !expanded ? (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                              </div>
                            ) : (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-up']} />
                              </div>
                            )
                          }
                        </AccordionItemState>
                      </div>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    <div className="trainingGrid sub1">{specialistLinks}</div>
                  </AccordionItemPanel>
                </AccordionItem>
              </div>
              {itineraryLinks.length > 0 && (
                <AccordionItem uuid="itineraries">
                  <div className="trainingGrid subApps">
                    <AccordionItemHeading>
                      <AccordionItemButton>
                        <div className="subheader goldHeader">
                          <h1>{i18n.translate('contentHeaders.itineraries')}</h1>
                          <AccordionItemState>
                            {({ expanded }) =>
                              !expanded ? (
                                <div className="arrow">
                                  <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                                </div>
                              ) : (
                                <div className="arrow">
                                  <FontAwesomeIcon icon={['fas', 'chevron-up']} />
                                </div>
                              )
                            }
                          </AccordionItemState>
                        </div>
                      </AccordionItemButton>
                    </AccordionItemHeading>
                  </div>
                  <AccordionItemPanel>
                    <div className="trainingGrid trainingGrid--extra trainingGrid--6-col">{itineraryLinks}</div>
                  </AccordionItemPanel>
                </AccordionItem>
              )}
              <AccordionItem uuid="extra">
                <div className="trainingGrid subApps">
                  <AccordionItemHeading>
                    <AccordionItemButton>
                      <div className="subheader goldHeader">
                        <h1>{i18n.translate('contentHeaders.subapps')}</h1>
                        {/* <h2>{i18n.translate('pageDescriptions.subapps')}</h2>
                        <SubProgressBar type="gold" progress={extraCompletionRate} /> */}
                        <AccordionItemState>
                          {({ expanded }) =>
                            !expanded ? (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-down']} />
                              </div>
                            ) : (
                              <div className="arrow">
                                <FontAwesomeIcon icon={['fas', 'chevron-up']} />
                              </div>
                            )
                          }
                        </AccordionItemState>
                      </div>
                    </AccordionItemButton>
                  </AccordionItemHeading>
                </div>
                <AccordionItemPanel>
                  <div className="trainingGrid trainingGrid--extra trainingGrid--6-col">{extraLearningLinks} </div>
                </AccordionItemPanel>
              </AccordionItem>
            </div>
          </Accordion>
        </React.Fragment>
      )}
    </Spring>
  );
};

export default RetainGrid;
